/**
 * Every decision the Users & Workspaces page makes, in one place with no Angular in it.
 *
 * The components render what these functions return and decide nothing themselves. That is what
 * makes the page's rules testable in Node, and it is why a rule appears once rather than in each
 * of the four templates that happen to need it.
 */
import type { Workspace, WorkspaceUser, WorkspaceInvitation, InvitationStatus, ProjectSwitches, RoleEvent } from './workspaces.service';
import type { MobileAccessMode } from './service-worker.util';

export type Access = 'owner' | 'editor' | 'viewer';
export interface Control { enabled: boolean; reason: string }

/**
 * The one sentence every owner-only control on this page refuses with. Exported, but NOT because a
 * component renders it directly — nothing outside this module does (fix-round-3 Minor 7: the comment
 * here used to claim "Task 4 renders it verbatim", which was never true and sent a reviewer looking
 * for a caller that does not exist). It stays exported because `workspaces.util.spec.ts` asserts that
 * `seatCapControl` and `projectSwitchControl` refuse with the SAME sentence rather than two of their
 * own — which is the whole reason it is a constant and not two string literals.
 */
export const OWNERS_ONLY = 'Only the project owner can change this.';

// ─── A workspace being deleted (account deletion: its last admin deleted their account) ───
//
// Pending, the workspace waits 30 days with its data and no members, then the server's daily job
// deletes it for good and keeps its row as a "deleted" marker. Throughout, nothing in it may change and
// nobody may join it; the server refuses both, and these say so before the click. Only a pending
// workspace can be restored, and only by a builder with edit access, which is the server's own rule.

export type WorkspaceDeletion = 'pending' | 'deleting' | 'deleted';

/** Where a workspace stands in deletion, or null when nobody is deleting it. */
export function workspaceDeletion(w: Workspace): WorkspaceDeletion | null {
  if (w.status === 'deleted') return 'deleted';
  if (w.deletionStartedAt) return 'deleting';
  if (w.pendingDeletionAt) return 'pending';
  return null;
}

export const PENDING_DELETION_CHIP = 'Pending deletion';
export const DELETED_CHIP = 'Deleted';
export const PENDING_READ_ONLY = 'This workspace is waiting to be deleted, so nothing in it can be changed.';
export const DELETED_READ_ONLY = 'This workspace has been deleted, so nothing in it can be changed.';
export const RESTORE_CONFIRM =
  'Its data comes back, but not its members: their accounts were deleted. Add an admin afterwards to manage it.';

/** The chip and the line under the name, or null for a workspace nobody is deleting. */
export function deletionNote(w: Workspace): { chip: string; line: string; deleted: boolean } | null {
  const state = workspaceDeletion(w);
  if (!state) return null;
  if (state === 'deleted') {
    const d = shortDate(w.deletedAt ?? w.deletionStartedAt ?? w.deleteAfter ?? null);
    return { chip: DELETED_CHIP, line: d ? `Deleted for good on ${d}` : 'Deleted for good', deleted: true };
  }
  if (state === 'deleting') {
    return { chip: PENDING_DELETION_CHIP, line: 'Being deleted for good now. It can no longer be restored.', deleted: false };
  }
  const d = shortDate(w.deleteAfter ?? null);
  return { chip: PENDING_DELETION_CHIP, line: d ? `Deleted for good on ${d}` : 'Waiting to be deleted for good', deleted: false };
}

/** Every change control on a workspace being deleted refuses with this, ahead of its own rules. */
export function deletionReadOnly(w: Workspace): Control | null {
  const state = workspaceDeletion(w);
  if (!state) return null;
  return { enabled: false, reason: state === 'deleted' ? DELETED_READ_ONLY : PENDING_READ_ONLY };
}

/** Restore is offered only while the workspace is pending, and only to owners and editors. */
export function canRestoreWorkspace(w: Workspace, access: Access): boolean {
  return workspaceDeletion(w) === 'pending' && (access === 'owner' || access === 'editor');
}

export const NO_ADMIN_HINT = 'No admin yet. Add someone and make them admin, so the workspace can be managed.';

/**
 * A workspace with no active admin has nobody who can manage it from inside the app, which is how a restored
 * workspace always comes back. The admin count needs the people list, which only an opened row loads; until then
 * the only certain case is a workspace nobody is in. Pass `people` as null while it is not loaded. Not shown while
 * the workspace is being deleted (nobody can be made admin there) or on the builders' own workspace, which the
 * builders manage from the editor.
 */
export function noAdminHint(w: Workspace, people: WorkspaceUser[] | null): string | null {
  if (workspaceDeletion(w) || w.isBuilders) return null;
  if (people) return activePeople(people).some((p) => p.roleId === 'admin') ? null : NO_ADMIN_HINT;
  return w.activeCount === 0 ? NO_ADMIN_HINT : null;
}

/**
 * The page's order: workspaces in use first, then those being deleted, then the deleted ones. Stable,
 * so the server's own order (by name) holds inside each group.
 */
export function workspacesInPageOrder(list: Workspace[]): Workspace[] {
  const rank = (w: Workspace) => {
    const state = workspaceDeletion(w);
    return state === 'deleted' ? 2 : state ? 1 : 0;
  };
  return list.map((w, i) => ({ w, i })).sort((a, b) => rank(a.w) - rank(b.w) || a.i - b.i).map((x) => x.w);
}


const BUILDERS_UNCAPPED =
  'The builders’ workspace has no seat limit — your team must always be able to get in.';

/**
 * WHY THE BUILDERS' WORKSPACE HAS NO RENAME. Its name is not decoration — `getDefaultTenantId`
 * (olympus-functions `utils/track-d.ts`) finds that workspace by matching the literal string
 * "Default (Editor)", and everything downstream of it (the Developer-mode gate on arrivals into it,
 * every row's own `isBuilders`, preview invite routing, Track-D role injection at sync) goes quiet
 * the moment the name moves. The server refuses the rename outright (`manageWorkspaces` returns 409);
 * this control exists so the row never OFFERS an affordance the server is certain to refuse — the
 * same shape as `seatCapControl` above, and for the same reason.
 */
export const BUILDERS_NAME_FIXED =
  'Your own team’s workspace keeps its name — your project finds it by that name.';

export function renameControl(w: Workspace): Control {
  const closed = deletionReadOnly(w);
  if (closed) return closed;
  return w.isBuilders ? { enabled: false, reason: BUILDERS_NAME_FIXED } : { enabled: true, reason: '' };
}

/**
 * Any non-viewer may create a role — `manage-role.function.ts` gates `createRole` on editor, not
 * owner, so this is deliberately NOT `OWNERS_ONLY`. Lives here rather than in
 * `roles-tab.component.ts`, where it shipped inline (fix-round-3 Minor 6): this module's own header
 * rule is that page copy appears once, here, and a sentence typed into a component is the drift this
 * file exists to prevent.
 */
export const ROLE_CREATE_NEEDS_EDIT = 'You need edit access on this project to create a role.';

/**
 * The four states a seat cap can be in, agreeing with the server's own arithmetic
 * (`seatCheck`/`seatOvershoot` in `olympus-functions/functions/src/workspaces/workspace-rules.ts`):
 * room exists while `activeCount < limit`, so `full` is reached at `activeCount === limit` and
 * `over` only once it is exceeded — exactly-at-capacity is full, never over.
 */
export function seatState(w: Workspace): 'uncapped' | 'room' | 'full' | 'over' {
  if (w.seatLimit === null || w.seatLimit === undefined) return 'uncapped';
  if (w.activeCount > w.seatLimit) return 'over';
  if (w.activeCount >= w.seatLimit) return 'full';
  return 'room';
}

/**
 * The editor's counterpart to the server's `seatCheck`: the two must agree, because `seatCheck`
 * refuses an arrival for BOTH `full` and `over`, not just `full`. A component that instead writes
 * the obvious `seatState(w) === 'full'` — to decide `invitationRow`'s `seatsFull`, or Task 9's
 * warning — silently admits the over-cap case, which is exactly where the server refuses hardest.
 * A workspace at or past its cap admits nobody; this is the one place that says so.
 */
export function seatsAreFull(w: Workspace): boolean {
  const state = seatState(w);
  return state === 'full' || state === 'over';
}

/** "person"/"people" — the one place this page decides that, so `seatLabel` and `seatOvershootLine`
 *  (Task 10, fix-round-1 finding 3) can never pluralise the same count two different ways. */
function personWord(n: number): string {
  return n === 1 ? 'person' : 'people';
}

/**
 * "8 of 20 seats", "8 people", and the two states a cap can be in — rendered from `seatState`
 * rather than re-deciding the boundary here, so a component checking "is this full" never has to
 * substring-match this copy or recompute the comparison itself.
 */
export function seatLabel(w: Workspace): string {
  const state = seatState(w);
  if (state === 'uncapped') {
    return `${w.activeCount} ${personWord(w.activeCount)}`;
  }
  const seatWord = w.seatLimit === 1 ? 'seat' : 'seats';
  const base = `${w.activeCount} of ${w.seatLimit} ${seatWord}`;
  if (state === 'over') return `${base} · over`;
  if (state === 'full') return `${base} · full`;
  return base;
}

/**
 * What `setSeatLimit`'s own response says a save just did, when it left the workspace over its new
 * cap — from THAT call's `activeCount`/limit, not a fresh read. States the fact only: it never repeats
 * "nobody was removed" (`SEAT_CAP_LOWERING_NOTE` already says that once, beside the control) — Task
 * 10, fix-round-1 finding 3 flagged the same rule appearing in two independent phrasings in the same
 * column, which drifts the moment one is edited and the other is not. Pluralises through `personWord`,
 * the same helper `seatLabel` uses, rather than a second `=== 1` check living here.
 */
export function seatOvershootLine(activeCount: number, limit: number): string {
  return `This workspace now holds ${activeCount} ${personWord(activeCount)}, over its new limit of ${limit}.`;
}

/**
 * The seat meter on every collapsed row — `seatLabel` drawn as a picture, so it reads its boundary
 * from `seatState` and can never disagree with the words printed beside it (exactly at the cap is
 * FULL, never over). A small cap is drawn one pip per seat, so "4 of 4" looks like four taken chairs
 * rather than a bar at 100%; past `SEAT_PIPS_MAX` a pip would be too thin to read, so it becomes a bar.
 * An over-cap workspace fills the meter and reports the excess in `overflow` rather than drawing past
 * the end of it. An uncapped workspace has no meter at all — there is nothing to be a fraction of.
 */
export const SEAT_PIPS_MAX = 24;
export interface SeatMeter {
  state: 'uncapped' | 'room' | 'full' | 'over';
  /** 0–100, clamped — an over-cap workspace draws a full meter, never a longer one. */
  percent: number;
  /** One entry per seat, `true` for a taken one — `null` when the cap is too large (or zero) to draw that way. */
  pips: boolean[] | null;
  /** People beyond the cap. Always 0 unless `state` is `over`. */
  overflow: number;
}
export function seatMeter(w: Workspace): SeatMeter {
  const state = seatState(w);
  if (state === 'uncapped') return { state, percent: 0, pips: null, overflow: 0 };
  const limit = w.seatLimit as number;
  const percent = limit > 0 ? Math.min(100, Math.round((w.activeCount / limit) * 100)) : 100;
  const pips = limit >= 1 && limit <= SEAT_PIPS_MAX
    ? Array.from({ length: limit }, (_, i) => i < w.activeCount)
    : null;
  return { state, percent, pips, overflow: Math.max(0, w.activeCount - limit) };
}

/**
 * The page's headline numbers, above the list. `people` sums the server's own `activeCount` — never a
 * count of loaded people, since most rows are never opened and so never loaded. `atCapacity` goes
 * through `seatsAreFull`, so a workspace sitting OVER its cap is counted too, exactly as the server
 * refuses an arrival into it.
 */
export interface WorkspaceTotals { workspaces: number; people: number; capped: number; atCapacity: number }
export function workspaceTotals(list: Workspace[]): WorkspaceTotals {
  let people = 0;
  let capped = 0;
  let atCapacity = 0;
  for (const w of list) {
    people += w.activeCount || 0;
    if (seatState(w) !== 'uncapped') capped++;
    if (seatsAreFull(w)) atCapacity++;
  }
  return { workspaces: list.length, people, capped, atCapacity };
}

/**
 * THE BUILDERS' WORKSPACE CAP IS DISABLED HERE ON PURPOSE, not because the server is missing it.
 * The server refuses it too (manageWorkspaces returns 409). It is disabled in the page as well so a
 * builder is told the rule instead of discovering it as a failure: a cap on the sandbox their own
 * team tests in could lock that team out of the only workspace they can reach.
 */
export function seatCapControl(w: Workspace, access: Access): Control {
  const closed = deletionReadOnly(w);
  if (closed) return closed;
  if (w.isBuilders) return { enabled: false, reason: BUILDERS_UNCAPPED };
  if (access !== 'owner') return { enabled: false, reason: OWNERS_ONLY };
  return { enabled: true, reason: '' };
}

/** Stopping sign-ups or invitations changes the app for everyone, so it is the owner's alone. */
export function projectSwitchControl(access: Access): Control {
  return access === 'owner' ? { enabled: true, reason: '' } : { enabled: false, reason: OWNERS_ONLY };
}

export const ROLE_CHANGE_VIEWERS = 'Only owners and editors can change a role.';
export const ROLE_CHANGE_OTHER_BUILDER = 'Only an owner can change another builder’s role.';

/**
 * Who may change a person's role from their row (2026-09-25, owner's call): owners AND editors —
 * editors already rewrite every role's grants, so which role a person holds is theirs to set too,
 * admin included. The builders' workspace is the one owner-only corner: a builder's app role there
 * is the value `setAppRole` guards with "the owner sets anyone's; everyone else sets only their
 * own", and `manageMembers setUserRole` refuses the same way. This only says so before the click.
 */
export function roleChangeControl(w: Workspace, access: Access, isSelf: boolean): Control {
  const closed = deletionReadOnly(w);
  if (closed) return closed;
  if (access !== 'owner' && access !== 'editor') return { enabled: false, reason: ROLE_CHANGE_VIEWERS };
  if (w.isBuilders && access !== 'owner' && !isSelf) return { enabled: false, reason: ROLE_CHANGE_OTHER_BUILDER };
  return { enabled: true, reason: '' };
}

export interface RoleOption { id: string; label: string; disabled: boolean; reason: string }

/** The two choices the server is certain to refuse, known here so the picker never offers them. */
export interface RoleLimits { lastAdmin: boolean; ownBuildersRow: boolean }
export const LAST_ADMIN_NOTE = "This is the workspace’s only admin — make someone else admin first.";
export const OWN_NO_ACCESS_NOTE = "You can’t set your own access to No access — another builder has to.";

/**
 * What a person's role picker offers: admin first, the project's own roles in `listRoles` order,
 * then "No access" last. `inactive` is the stored id for No access (see `roleIsNoAccess`) — offered
 * under that name only, never as a role called "inactive". A person holding an id the list does not
 * know (a deleted role, or the list failed to load) keeps it as an option, so the picker never
 * silently shows them as something they are not.
 *
 * `limits` disables what the server refuses (the page rule): every non-admin choice for a workspace's
 * last admin (`provisionUserCore`'s last-admin invariant), and No access on a builder's own row in the
 * builders' workspace (`NO_ACCESS_IS_OWNERS` in manageMembers).
 */
export function roleOptions(
  names: ReadonlyMap<string, string> | null | undefined,
  currentRoleId: string,
  limits: RoleLimits = { lastAdmin: false, ownBuildersRow: false },
): RoleOption[] {
  const ids: { id: string; label: string }[] = [{ id: 'admin', label: names?.get('admin') || 'Admin' }];
  for (const [id, label] of names ?? []) {
    if (id !== 'admin' && !roleIsNoAccess(id)) ids.push({ id, label });
  }
  if (!roleIsNoAccess(currentRoleId) && !ids.some((o) => o.id === currentRoleId)) {
    ids.push({ id: currentRoleId, label: currentRoleId });
  }
  ids.push({ id: 'inactive', label: 'No access' });
  return ids.map((o) => {
    if (limits.lastAdmin && o.id !== 'admin') return { ...o, disabled: true, reason: LAST_ADMIN_NOTE };
    if (limits.ownBuildersRow && o.id === 'inactive') return { ...o, disabled: true, reason: OWN_NO_ACCESS_NOTE };
    return { ...o, disabled: false, reason: '' };
  });
}

/** The same count `removeFromWorkspace` and the last-admin invariant make: ACTIVE admins only. */
export function isLastAdmin(personRow: WorkspaceUser, people: WorkspaceUser[]): boolean {
  if (personRow.roleId !== 'admin') return false;
  return activePeople(people).filter((p) => p.roleId === 'admin').length === 1;
}

/** Granting admin always asks first — on a row and on a username add, in these same words. */
export const ADMIN_GRANT_TITLE = 'Make them an admin?';
export function adminGrantBody(personName: string, workspaceName: string): string {
  return `${personName} will be able to do everything in ${workspaceName}, and add, remove and change the role of anyone in it.`;
}

/** A pending invitation's role is fixed until they join; this says where it can change. */
export const INVITE_ROLE_LATER = 'You can change their role once they accept.';

/** The picker's selected value — a blank roleId and `inactive` are ONE state (`roleIsNoAccess`). */
export function roleValue(roleId: string | null | undefined): string {
  return roleIsNoAccess(roleId) ? 'inactive' : String(roleId);
}

/**
 * Builder-facing names for the stored mode values. `pwa_only` is a column value; nobody outside the
 * code should ever read it. Same discipline as calling a tenant a Workspace.
 */
export const MODE_LABELS: Record<MobileAccessMode, string> = {
  off: 'Anyone, in a browser',
  pwa_only: 'Installed web app only',
  native_only: 'Mobile app only',
};

export const MODE_NOTES: Record<MobileAccessMode, string> = {
  off: 'Your app opens normally in a phone browser. Nothing changes.',
  pwa_only: 'On a phone, your app opens only once someone has added it to their home screen. In a browser they are shown how to install it.',
  native_only: 'On a phone, your app opens only in your Android or iPhone app. A phone browser is told the app is not available there.',
};

/** Shown before the change is applied, not after — this one strands people. */
export const NATIVE_ONLY_WARNING =
  'Anyone who already installed the web app to their home screen will lose access to it. They will need your Android or iPhone app instead.';

/**
 * The seat cap's own explanation, exported so a component never retypes it (Task 10, fix-round-1
 * finding 3 — this used to be typed straight into the template, the third time this plan has had page
 * copy shipped that way). Shown once, beside the control, regardless of its state — lowering a cap is
 * the one seat behaviour builders otherwise guess wrong, so it says so up front rather than leaving
 * `seatOvershootLine` below to be the only place that ever explains it, and only after the fact.
 */
export const SEAT_CAP_LOWERING_NOTE =
  'Lowering this never removes anyone. It only refuses the next person until the workspace is back under the limit.';

/** What each workspace switch means when it IS open — shown only when `switchDescription` (below)
 *  finds no shut-door reason to show instead. */
export const ALLOW_INVITATIONS_COPY = 'People can be invited by email into this workspace.';
export const ALLOW_DIRECT_ADDS_COPY = 'You can create a username and password for someone in this workspace.';

/** ws 9b-2 W14e — the Arrival notice's switch on a workspace row. On by default; owner or editor sets it. */
export const ARRIVAL_NOTICE_LABEL = 'Email admins when someone joins';
export const ARRIVAL_NOTICE_COPY = 'Admins of this workspace get an email when someone joins it.';
export function arrivalNoticeOn(w: Workspace): boolean { return w.arrivalNoticeEnabled !== false; }

/**
 * What a component prints under a workspace switch — Task 10, fix-round-1 finding 1 (CRITICAL): the
 * strip used to print `ALLOW_INVITATIONS_COPY`/`ALLOW_DIRECT_ADDS_COPY` unconditionally, whatever
 * `doorsInto` actually decided, so a project-wide switch off (or the builders' workspace with
 * Developer mode off) left the strip claiming a door was open — beside a form, six lines below on the
 * SAME screen, reading the very same `doorsInto` call and saying the door was shut. `door.reason` is
 * empty exactly when the door is genuinely open (see `doorsInto`'s own return shape), so preferring it
 * over the open-copy is never a false negative: a component calls this instead of writing the `||`
 * itself, so the choice can't drift from what `doorsInto` decided.
 */
export function switchDescription(door: Control, openCopy: string): string {
  return door.reason || openCopy;
}

/**
 * Why Resend is greyed out on this workspace's pending invitations — fix-round-3 Important 3.
 *
 * Resend IS `inviteUser` again, so it goes through the very same door as a new invitation: the
 * project switch, this workspace's own switch, or (for the builders' workspace) Developer mode. The
 * row used to offer it from `invitationRow(...).actions` alone, which only knows the invitation is
 * pending, and the server answered with `INVITES_OFF` — "switched off for this app" — even when what
 * the builder had actually switched off was this ONE workspace, which the strip six lines above was
 * simultaneously describing correctly. Two sentences about one switch, on one screen.
 *
 * Composed from `doorsInto`'s own verdict, never from a second reading of the switches, and shaped
 * like `USERNAME_SEAT_FULL` + `USERNAME_SEAT_FULL_INVITE_SUGGESTION`: the door supplies the reason,
 * this adds only what is true whatever the reason was. Empty when the door is open — there is
 * nothing to explain.
 */
export const RESEND_BLOCKED_SUFFIX = ' Resend stays unavailable until that changes.';
export function resendBlockedLine(door: Control): string {
  return door.enabled ? '' : door.reason + RESEND_BLOCKED_SUFFIX;
}

export const INVITES_OFF_PROJECT = 'Invitations are switched off for this project.';
export const INVITES_OFF_WORKSPACE = 'Invitations are switched off for this workspace.';
export const ADDS_OFF_WORKSPACE = 'Direct adds are switched off for this workspace.';
export const BUILDERS_NEEDS_DEV_MODE =
  "This is your team's own workspace — turn on Developer mode to add anyone to it.";

/**
 * The username mode's own seat-full message (`AddPersonComponent.usernameSeatControl`, the only
 * reader) — moved here (fix-round-2 finding 1) after it shipped hardcoded in the component, which
 * is exactly the drift `doorsInto` and its reason constants above were meant to prevent, and
 * `doorsInto`'s own comment already promises Task 10 needs these sentences a third time. A
 * username/password account is created (and takes its seat) immediately, so a full workspace is
 * CERTAIN to fail here, not merely possible — unlike the email mode, where a seat is only taken at
 * acceptance. Addressed to the administrator directly: the server's own refusal for this
 * (`workspace-rules.ts`'s `FULL`) is written for someone ACCEPTING an invite link, not for the
 * builder creating an account by hand.
 *
 * Split into a base sentence and a separate, optional suggestion (fix-round-2 finding 2): the
 * "invite by email instead" clause is only true when that door is actually open, and the caller
 * must decide that by reading `doorsInto`'s own verdict — never by re-deciding it here.
 */
export const USERNAME_SEAT_FULL = 'This workspace is full. Free a seat or raise the limit before creating an account.';
export const USERNAME_SEAT_FULL_INVITE_SUGGESTION =
  ' Invite by email instead — an invitation does not need one until it is accepted.';

/**
 * Which doors into a workspace are open — Task 9's own gating, moved here (fix-round-1 finding 5)
 * after it was typed twice already (the add-person form and this row's invitations empty state)
 * with copy that had already drifted apart, and Task 10 needs the same sentences a third time. Every
 * "why is this closed" surface on this page must read the same three facts through here rather than
 * re-deciding any of them:
 *
 *  - Invitations: the PROJECT switch, then this workspace's own — the project fact wins, because it
 *    is the more general one and does not stop being true just because this one workspace's own
 *    switch happens to be on.
 *  - Direct adds: a WORKSPACE-only switch. There is no project-wide equivalent.
 *  - The builders' own workspace (`Workspace.isBuilders`) is a THIRD door, checked ahead of both
 *    switches: `inviteUser` and `manageUserAccount` both resolve the arrival's target through
 *    `resolveUserTarget` (olympus-functions `tenant/user-target.ts`) BEFORE either switch is even
 *    read, and that function refuses ANY arrival into the builders' workspace while Developer mode
 *    is off — full stop, whatever the switches say. It is also the one workspace every project has
 *    and the one a builder is likeliest to try first, so it closes BOTH doors with one reason rather
 *    than leaving the switch-based checks to report a false "open".
 *
 * `opts.developerModeOn` missing/undefined reads as OFF, not "unknown" — a wrong "closed" costs a
 * builder one confusing click; a wrong "open" costs them a typed password and a 409 addressed to
 * "your administrator" when they ARE the administrator (fix-round-1 finding 2).
 */
export function doorsInto(
  w: Workspace, switches: ProjectSwitches | null, opts?: { developerModeOn?: boolean }
): { invite: Control; add: Control } {
  // Ahead of every other door: the server refuses every way into a workspace being deleted.
  const closed = deletionReadOnly(w);
  if (closed) return { invite: closed, add: closed };
  if (w.isBuilders && !opts?.developerModeOn) {
    return {
      invite: { enabled: false, reason: BUILDERS_NEEDS_DEV_MODE },
      add: { enabled: false, reason: BUILDERS_NEEDS_DEV_MODE },
    };
  }
  const invite: Control = switches?.invitesDisabled
    ? { enabled: false, reason: INVITES_OFF_PROJECT }
    : !w.invitesEnabled
      ? { enabled: false, reason: INVITES_OFF_WORKSPACE }
      : { enabled: true, reason: '' };
  const add: Control = w.addsEnabled
    ? { enabled: true, reason: '' }
    : { enabled: false, reason: ADDS_OFF_WORKSPACE };
  return { invite, add };
}

/**
 * A falsy `roleId` and `'inactive'` are ONE state to the server, not two — `gateway.function.ts`
 * denies BOTH with the same "Account not activated: no role assigned yet". Shared here so nothing
 * that renders a role — a `WorkspaceUser`'s pill, a `WorkspaceInvitation`'s role line — decides this
 * boundary on its own; two call sites deciding it independently is how a builder ends up reading the
 * same empty roleId described two different ways in the same expanded row (fix-round-1 finding 2).
 */
export function roleIsNoAccess(roleId: string | null | undefined): boolean {
  return !roleId || roleId === 'inactive';
}

/** What the roles list row prints for a role's grant count, and whether that count is a warning. */
export interface RoleAccessLabel { text: string; warn: boolean }

/**
 * `admin` skips this entirely — the caller's own `r.id !== RESERVED_ADMIN` guard, unchanged. Every
 * OTHER role only reaches an entity through an explicit grant the gateway checks per-entity, so a
 * count of zero is not "unconfigured, get to it later" — it is fatal. Someone signed into a
 * zero-permission role authenticates fine, then the app hangs on its loading screen forever with no
 * error, because its first read (`systemOotbUser`) is denied. Three real End users hit exactly this
 * on 2026-09-21. So zero does not render as the same quiet grey count as every other number; it
 * renders as a named warning, and that decision is made here, once, so the row only ever displays
 * what this returns.
 */
export function roleAccessLabel(permissionCount: number): RoleAccessLabel {
  if (permissionCount === 0) {
    return { text: 'No permissions — nobody with this role can use the app until it is given some.', warn: true };
  }
  return { text: `${permissionCount} permission(s)`, warn: false };
}

/**
 * What a role pill says: the role's NAME where the page has one, its stored id otherwise. People and
 * invitations carry only `roleId` ("sale_executive_l1"), and printing that raw is what made the rows
 * read like a database dump. The fallback is the id itself, deliberately unprettified — a role the
 * list does not know (it failed to load, or the role is newer than the list) must still show exactly
 * what the person holds rather than a guess at it. Never reached for the no-access state:
 * `roleIsNoAccess` decides that first, and a blank id has no name to find.
 */
export function roleName(roleId: string, names: ReadonlyMap<string, string> | null | undefined): string {
  return names?.get(roleId) || roleId;
}

/**
 * listUsers RETURNS REMOVED PEOPLE TOO — that is deliberate, so the page can show them behind its
 * own filter. Any count taken from that list must come through here, or it will disagree with the
 * activeCount the server computes and every seat decision is made against.
 *
 * Someone set to `inactive` ("No access") still holds their seat — this filters on `removedAt`
 * alone, never on `roleId`, because a seat reflects who is IN the workspace, not who is currently
 * allowed through the door.
 */
export function activePeople(users: WorkspaceUser[]): WorkspaceUser[] {
  return users.filter((u) => !u.removedAt);
}
export function removedPeople(users: WorkspaceUser[]): WorkspaceUser[] {
  return users.filter((u) => !!u.removedAt);
}

/** Erased people are also removed, so they sit in the Removed list — this is what tells them apart. */
export function isErased(u: { erasedAt?: string | null }): boolean {
  return !!u.erasedAt;
}

/** Name, email and username. NEVER role — searching "manager" looks for a person, not a job. */
export function matchesPerson(u: WorkspaceUser, term: string): boolean {
  const q = term.trim().toLowerCase();
  if (!q) return true;
  return [u.userName, u.email, u.loginUsername]
    .some((v) => (v || '').toLowerCase().includes(q));
}

export function matchesWorkspace(w: Workspace, term: string): boolean {
  const q = term.trim().toLowerCase();
  if (!q) return true;
  return (w.name || '').toLowerCase().includes(q);
}

export interface FilteredWorkspace {
  visible: boolean; forceOpen: boolean; people: WorkspaceUser[]; removed: WorkspaceUser[]; countLabel: string;
  /**
   * WHETHER `people`/`removed` ARE A NARROWING, or simply everybody on the record.
   *
   * Fix-round-3 Minor 5: the two consumers of this shape both DOCUMENTED "null for a workspace
   * matched by its OWN name" while their code handed the row a full `Set` for that case, and the
   * reviewer's verdict was that the comment "made finding 2 invisible to five reviewers". Rather
   * than fix the prose, the distinction the prose was describing is now a field the caller can read:
   *
   *  - `false` — an empty term, or a workspace matched BY ITS NAME. Both lists hold everyone, no
   *    count is shown, and a row must render its people unfiltered.
   *  - `true` — matched through its PEOPLE. Both lists are the hits only, and `countLabel` says so.
   *
   * A caller deriving "which ids matched" must return null (meaning: narrow nothing) whenever this
   * is false. Nothing may infer that from an empty `countLabel` instead — a narrowed workspace with
   * no hits at all is invisible, so those two facts happen to coincide today and would stop
   * coinciding the moment either rule moved.
   */
  narrowed: boolean;
}

/**
 * What a search term does to one workspace.
 *
 * Search reaches active AND removed people — a removed person is still a person the page can show,
 * behind Task 7's "Removed (n)" panel, and a search that only looked at active people would make a
 * removed match unfindable while hiding the whole workspace with nothing saying why. A workspace
 * matched BY NAME shows everybody in it, both lists, unfiltered — you asked for the workspace, not
 * for a person in it — and so has no "n of m" count. A workspace matched only through its people
 * narrows both lists to the hits and counts them over everyone on the record (active + removed).
 *
 * `forceOpen` (not `open`): it means "the search wants this row expanded", not "this row is open" —
 * it is always false on an empty term, and a component must not use it to collapse a row the builder
 * opened by hand.
 */
export function filterWorkspace(w: Workspace, users: WorkspaceUser[], term: string): FilteredWorkspace {
  const all = activePeople(users);
  const removedAll = removedPeople(users);
  const q = term.trim();
  if (!q) {
    return { visible: true, forceOpen: false, people: all, removed: removedAll, countLabel: '', narrowed: false };
  }
  if (matchesWorkspace(w, q)) {
    return { visible: true, forceOpen: true, people: all, removed: removedAll, countLabel: '', narrowed: false };
  }
  const activeHits = all.filter((u) => matchesPerson(u, q));
  const removedHits = removedAll.filter((u) => matchesPerson(u, q));
  const totalHits = activeHits.length + removedHits.length;
  if (!totalHits) {
    return { visible: false, forceOpen: false, people: [], removed: [], countLabel: '', narrowed: true };
  }
  const totalPeople = all.length + removedAll.length;
  return {
    visible: true, forceOpen: true, people: activeHits, removed: removedHits,
    countLabel: `${totalHits} of ${totalPeople} match`, narrowed: true,
  };
}

/**
 * Which people ids a search row should narrow to — `null` means NARROW NOTHING, show everybody the
 * row has loaded. Hoisted out of `WorkspacesTabComponent.matchedUserIdsFor` so the decision that was
 * a live Critical (see below) has a spec.
 *
 * `null` is the honest answer in three situations: no search has run at all (`entry` is `undefined`),
 * an empty term, or a workspace whose `FilteredWorkspace.narrowed` is `false` — either matched by its
 * OWN name (everybody in it is a result) or its `listUsers` call FAILED (the `applySearch` sentinel:
 * `{ people: [], removed: [], narrowed: false }`).
 *
 * It reads `narrowed` rather than testing `!entry`, because `!entry` was the bug this replaces: an
 * entry EXISTS for both the name-match and the load-failure case, so `!entry` is false for both and
 * the old code fell through to building a Set either way — an everybody-Set for the name match
 * (harmless, why nobody noticed) and an EMPTY Set for the failure. A workspace whose people loaded
 * fine then filtered itself down to nothing and rendered "No active match here for this search —
 * check Removed below," directly under a banner already admitting the search could not finish.
 */
export function matchedUserIds(entry: FilteredWorkspace | undefined): Set<string> | null {
  if (!entry || !entry.narrowed) return null;
  return new Set([...entry.people, ...entry.removed].map((u) => u.id));
}

/**
 * "Nothing matches “bo”." (Task 11's no-results state) — exported so the template never retypes
 * page copy (this module's own header rule, enforced three times already in this plan) with the
 * curly quotes typed straight into the HTML. Trims the same way `matchesPerson`/`matchesWorkspace`
 * do, so a term with incidental leading/trailing space never renders with it still attached.
 */
export function noSearchResultsMessage(term: string): string {
  return `Nothing matches “${term.trim()}”.`;
}

/**
 * Whether Task 11's no-results block should show — a pure decision, not a template expression,
 * fixed here after fix-round-1 found two ways a template-level `*ngIf` got it wrong:
 *
 *  - Critical 1: a cleared term must NEVER show this, however stale data from an abandoned search
 *    happens to be sitting around. The actual bug there was a missing sequence token in
 *    `WorkspacesTabComponent.applySearch` letting a slow, superseded response overwrite what a
 *    later clear had already reset — this function's own `!term.trim()` check is a second,
 *    independent backstop: even if a caller somehow still holds a stale "every row hidden" snapshot
 *    next to an empty term, the empty term alone is enough to refuse the claim.
 *  - Critical 2: a workspace whose load FAILED is not a workspace that was checked and found empty.
 *    `anyLoadFailed` refuses the whole block in that case — stating "nothing matches" is a confident
 *    negative, and a page that could not actually look everywhere has no business being confident.
 *    (The workspace itself must ALSO stay visible rather than vanish — that half of the fix is in
 *    `applySearch`, which never marks a failed workspace as `visible: false`; this function only
 *    covers the message.)
 */
export function shouldShowNoResults(opts: {
  term: string; workspaceCount: number; visibleCount: number; anyLoadFailed: boolean;
}): boolean {
  if (!opts.term.trim()) return false;
  if (opts.workspaceCount === 0) return false;
  if (opts.anyLoadFailed) return false;
  return opts.visibleCount === 0;
}

/**
 * Shown alongside the search results (never in place of them — a failed workspace stays visible,
 * per `shouldShowNoResults`'s own comment) whenever fetching even one workspace's people for a
 * search failed. Exported so this page copy lives here, not typed into the template.
 */
export const SEARCH_LOAD_INCOMPLETE =
  'Some workspaces could not be searched just now — what you see below may be missing a match. Try again in a moment.';

/**
 * What the ROW itself says when it is one of the workspaces that banner is about — fix-round-3
 * Critical 2. The page-level banner names the situation; this names the workspace it happened to,
 * inside the row, where the people are.
 *
 * It exists because the alternative was a confident lie. A failed workspace's search result used to
 * narrow that row to an empty set of matched ids, so a row holding fourteen successfully-loaded
 * people rendered none of them and stated "No active match here for this search" — a negative the
 * data never supported, six inches under a banner admitting the search could not look everywhere.
 * A row that could not be searched now narrows NOTHING (its ids come back null), shows everybody,
 * and says this instead.
 */
export const SEARCH_WORKSPACE_NOT_CHECKED =
  'This workspace could not be searched just now, so everyone in it is shown — any of them may or may not match.';

export interface InvitationRow {
  label: string; hint: string; actions: ('resend' | 'cancel')[]; inHistory: boolean;
}

const INVITATION_COPY: Record<InvitationStatus, { label: string; hint: string }> = {
  pending: { label: 'Waiting', hint: 'Sent. They have not used it yet.' },
  accepted: { label: 'Accepted', hint: 'They joined this workspace.' },
  linked_elsewhere: {
    label: 'In another workspace',
    hint: 'They hold a place in another workspace of this app. This invitation becomes usable the moment they leave it.',
  },
  removed: { label: 'Removed', hint: 'They were in this workspace and are not any more.' },
  void: { label: 'Cancelled', hint: 'Cancelled, or replaced by a newer invitation to the same address.' },
};

/**
 * Only a waiting invitation can be acted on; every other state is a fact, not a task.
 *
 * A PENDING invitation into a workspace that is currently full still needs to say so on the row
 * itself (user story 4: "an invitation that cannot be accepted tells me why on the list itself") —
 * without this, the only explanation a builder ever sees is the End user's own failed attempt to
 * accept it. Being full is not the invitee's fault, so resend/cancel stay available; the invitation
 * itself is neither voided nor queued, it simply waits for a seat.
 * Expired, not sent (held until publish) and couldn't send (ws 9b-1) are said before anything else.
 */
export function invitationRow(i: WorkspaceInvitation, opts?: { seatsFull?: boolean }): InvitationRow {
  // ws 9b-1 W1/W2/W4 — NOTHING IS SHOWN AS SENT THAT WAS NOT. These come before "workspace full": an
  // invitation that never reached anyone, or no longer works, has that to fix first.
  if (i.status === 'pending' && i.expired) {
    return { label: 'Expired', hint: 'Expired — resend to try again.', actions: ['resend', 'cancel'], inHistory: false };
  }
  if (i.status === 'pending' && i.emailOutcome === 'held') {
    // Publishing sends it. Resend is offered too (final review F3): on a project published since, a held row
    // would otherwise be stuck until the next publish; on an unpublished one the server keeps it held and says so.
    // Was (2026-10-07, ws 9b-1 final review): actions: ['cancel'],
    return { label: 'Not sent', hint: 'Not sent — it will be emailed when you first publish.', actions: ['resend', 'cancel'], inHistory: false };
  }
  if (i.status === 'pending' && i.emailOutcome === 'unsent') {
    // Born while the person held a place elsewhere: recorded, deliberately not mailed; only Resend sends it.
    return { label: 'Not sent', hint: 'Not sent yet — Resend to send it now.', actions: ['resend', 'cancel'], inHistory: false };
  }
  if (i.status === 'pending' && i.emailOutcome === 'failed') {
    return { label: "Couldn't send", hint: "The email couldn't be sent. Resend to try again.", actions: ['resend', 'cancel'], inHistory: false };
  }
  const pending = i.status === 'pending';
  if (pending && opts?.seatsFull) {
    return {
      label: 'Waiting — workspace full',
      hint: 'This workspace is full. The invitation stays valid and will work as soon as a seat frees. Raise the seat limit or remove somebody to make room.',
      actions: ['resend', 'cancel'],
      inHistory: false,
    };
  }
  const copy = INVITATION_COPY[i.status] || { label: i.status, hint: '' };
  // Was (2026-10-06, ws 9a W5): return { ...copy, actions: pending ? ['resend', 'cancel'] : [], inHistory: !pending };
  // A "linked elsewhere" invitation can be cancelled too (it would otherwise revive when the person leaves
  // their other workspace); it keeps its copy and stays in history.
  const actions: InvitationRow['actions'] = pending ? ['resend', 'cancel'] : i.status === 'linked_elsewhere' ? ['cancel'] : [];
  return { ...copy, actions, inHistory: !pending };
}

/**
 * The removal confirm modal's explanation of what happens to the person's record and seat — one
 * sentence, true for BOTH ways a person can be in a workspace. `reinstateInWorkspace` on the server
 * matches on email OR username, so "carries on from the same record" is a promise this page can make
 * to either kind of person — but "invite them back" names only the email arm. A username-only person
 * (created via the "add" mode, no email address at all) has nothing to be invited back TO, so that
 * wording was a promise the sentence could not keep for them. "Add them back" covers both doors this
 * page actually has into a workspace, matching `doorsInto`'s own naming of them (`invite` / `add`).
 */
export const REMOVE_PERSON_CONSEQUENCES =
  'Their sign-in is deleted immediately and their seat is freed. Everything they created stays, with ' +
  'their name still on it. Their username stays theirs for ever. If you add them back to this ' +
  'workspace they carry on from the same record.';

/**
 * The quiet markers on a collapsed row: only what is unusual, never "everything is fine".
 *
 * The marker set is the UNION of two independent facts, read two different ways on purpose:
 *
 *  - This workspace's OWN `invitesEnabled`/`addsEnabled` columns — read directly, not through
 *    `doorsInto`. That is deliberate: a column is a fact about THIS workspace and stays true
 *    regardless of what the project-wide switch is doing, but `doorsInto`'s own precedence has the
 *    project fact WIN when both are off ("the project fact wins... does not stop being true just
 *    because this one workspace's own switch happens to be on" — its own comment). Reading its
 *    `reason` here would inherit that precedence and make the workspace's own setting vanish from
 *    the marker the moment the project switch also closed — turn the project switch off and every
 *    workspace that had ALSO switched itself off goes quiet at the same time, which is not what
 *    changed. A marker must not be a fact that a page state it isn't about can silently erase.
 *  - The builders' workspace needing Developer mode — read THROUGH `doorsInto`, because that is a
 *    THIRD door with no column of its own: `isBuilders` + Developer mode off shuts both doors
 *    regardless of what `invitesEnabled`/`addsEnabled` say, so nothing but `doorsInto` knows it.
 *    Before this, that workspace could carry both columns `true` and show no marker at all:
 *    collapsed, it looked like the most open workspace on the page; expanded, every surface on it
 *    explained the opposite.
 *
 * Deliberately does NOT surface `INVITES_OFF_PROJECT`: the project-wide switch is already stated once,
 * at the top of the page, by the switch strip itself ("Allow invitations — Off" plus its description).
 * Stamping the same fact onto every collapsed row would be noise for something already on screen — a
 * workspace whose own columns are BOTH on, with only the project switch shut, carries no marker at all.
 */
export function workspaceMarkers(
  w: Workspace, switches: ProjectSwitches | null, opts?: { developerModeOn?: boolean },
): string[] {
  const out: string[] = [];
  // A workspace being deleted carries its own chip (deletionNote); its door markers would only repeat it.
  if (workspaceDeletion(w)) return out;
  // HIDDEN 2026-09-25 (owner's call): a workspace's `status` changes nothing today — no sign-in,
  // gateway or seat check reads it, and nothing in the editor can set it — so an "Inactive" chip
  // only ever told builders something false. Restore this line once status actually gates access.
  // if (w.status && w.status !== 'active') out.push('Inactive');
  const doors = doorsInto(w, switches, opts);
  if (doors.invite.reason === BUILDERS_NEEDS_DEV_MODE || doors.add.reason === BUILDERS_NEEDS_DEV_MODE) {
    // One workspace-specific reason shuts both doors at once — say it once, not as two separate
    // (and misleadingly column-shaped) markers.
    out.push('Needs Developer mode');
  }
  if (!w.invitesEnabled) out.push('Invitations off');
  if (!w.addsEnabled) out.push('Direct adds off');
  return out;
}

/**
 * "14 Sep 2026" — a short absolute date in UTC, or '' for nothing/unparseable. Pulled out of
 * `auditLine` so every other place on this page that renders a bare date (Task 7's removed-person
 * date; Tasks 8-10's invitation dates) shares one format and one answer to "what if this string
 * doesn't parse" instead of a reimplementation per component — this module's own header says
 * components decide nothing themselves, and a date format is a decision.
 */
export function shortDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${d.getUTCDate()} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/**
 * "Changed by X, 14 Sep 2026" — and nothing at all when nobody has touched the control (no `by`, or
 * no `at` was ever recorded). When a change genuinely happened but its timestamp cannot be parsed,
 * this still names who did it and omits only the date — a real change never renders as silence.
 */
export function auditLine(by: string | null, at: string | null): string {
  if (!by || !at) return '';
  const when = shortDate(at);
  if (!when) return `Changed by ${by}`;
  return `Changed by ${by}, ${when}`;
}

// ─── The role trail, in words (owner, 2026-09-25). The server stores facts; these say them. ───

/** "Priya (builder)" or "Anand (Acme’s admin)" — the owner needs to tell the two apart. */
export function roleActorLabel(e: RoleEvent): string {
  if (e.actorKind === 'builder') return `${e.actorName} (builder)`;
  if (e.actorKind === 'workspace_admin') return e.actorWorkspace ? `${e.actorName} (${e.actorWorkspace}’s admin)` : `${e.actorName} (workspace admin)`;
  if (e.actorKind === 'zavo') return 'Zavo';
  return e.actorName;
}
function trailReason(e: RoleEvent): string { return e.reason ? ` ${e.reason}` : ''; }
function trailDated(text: string, at: string): string { const d = shortDate(at); return d ? `${text}, ${d}` : text; }

/** The one line under a person: their latest role-setting entry. Empty when there is none. */
export function roleTrailLine(e: RoleEvent | null | undefined): string {
  if (!e) return '';
  if (e.event === 'arrived' || e.event === 'returned') {
    const verb = e.event === 'arrived' ? 'Joined' : 'Came back';
    if (e.actorKind === 'self') return trailDated(`${verb} by signing up`, e.at);
    if (e.actorKind === 'invitation') return trailDated(`${verb} by invitation from ${e.actorName}`, e.at);
    return trailDated(`${e.event === 'arrived' ? 'Added' : 'Brought back'} by ${roleActorLabel(e)}${trailReason(e)}`, e.at);
  }
  if (e.event === 'erased') return trailDated(`Erased by ${roleActorLabel(e)}${trailReason(e)}`, e.at);
  if (e.event === 'workspace_restored') return trailDated(`Workspace restored by ${roleActorLabel(e)}${trailReason(e)}`, e.at);
  if (e.event === 'removed') return trailDated(`Removed by ${roleActorLabel(e)}${trailReason(e)}`, e.at);
  return trailDated(`Role set by ${roleActorLabel(e)}${trailReason(e)}`, e.at);
}

export interface RoleHistoryEntry { what: string; who: string; when: string }
export function roleHistoryEntry(e: RoleEvent, names: ReadonlyMap<string, string> | null | undefined): RoleHistoryEntry {
  const role = (id: string | null) => (roleIsNoAccess(id) ? 'No access' : roleName(String(id), names));
  const what =
    e.event === 'arrived' ? `Joined as ${role(e.toRoleId)}`
    : e.event === 'returned' ? `Came back as ${role(e.toRoleId)}`
    : e.event === 'removed' ? 'Removed from the workspace'
    : e.event === 'erased' ? 'Erased — their details were cleared'
    : e.event === 'workspace_restored' ? 'Workspace restored — its data came back, without its members'
    : `${role(e.fromRoleId)} → ${role(e.toRoleId)}`;
  const who =
    e.actorKind === 'self' ? 'by signing up'
    : e.actorKind === 'invitation' ? `by invitation from ${e.actorName}`
    : `by ${roleActorLabel(e)}${trailReason(e)}`;
  return { what, who, when: shortDate(e.at) };
}

/** Said at the top of every history, so an empty one never reads as "nothing ever happened". */
export function roleHistorySince(startedAt: string | null): string {
  const d = shortDate(startedAt);
  return d
    ? `Role changes are recorded from ${d}. Anything earlier was not.`
    : 'Role changes are recorded from when this history began. Anything earlier was not.';
}

/** W9 (2026-10-06) — a role's permission count counts only entities it may actually do something with; a row with
 *  every box unticked grants nothing, so a role made only of those gets the "No permissions" warning. */
export function grantedCount(permissions: Record<string, { create: boolean; read: boolean; update: boolean; delete: boolean }> | undefined): number {
  return Object.values(permissions || {}).filter((p) => p.create || p.read || p.update || p.delete).length;
}

/** ws 9b-3 W11 — owners and editors edit a person's name and add an email; viewers see only. */
export function canEditDetails(access: Access): boolean { return access === 'owner' || access === 'editor'; }
/** Add email is for someone with no email yet (a username person). Changing an existing email is not offered — the server refuses it. */
export function canAddEmail(u: WorkspaceUser, access: Access): boolean { return canEditDetails(access) && !String(u.email || '').trim(); }
export function nameDraftError(name: string): string { return String(name || '').trim() ? '' : 'Enter a name.'; }
export function emailDraftError(email: string): string {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(email || '').trim()) ? '' : 'Enter a valid email address.';
}
export const ADD_EMAIL_NOTE = "They'll get a link to confirm it. After that they can sign in with their username or this email.";
export function linkSentLine(email: string): string { return `Link sent to ${String(email || '').trim()}.`; }
